import React from 'react';
import {
  User,
  GraduationCap,
  Users,
  CheckCircle,
  TrendingUp,
  Calculator,
  Building,
  Cpu,
  Eye,
} from 'lucide-react';

export default function OrganizationStatus() {
  return (
    <section className="px-4 py-24 bg-gradient-to-br from-gray-950 to-gray-900 sm:px-6 lg:px-8 lg:py-32 relative overflow-hidden bg-[url('/images/bg2.jpg')] bg-cover bg-center bg-no-repeat">
      {/* 배경 장식 요소 */}
      <div className="absolute top-20 right-10 w-32 h-32 bg-blue-500/10 rounded-full blur-xl" />
      <div className="absolute bottom-20 left-10 w-40 h-40 bg-indigo-500/10 rounded-full blur-xl" />
      <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-cyan-500/10 rounded-full blur-lg" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* 제목 */}
        <div className="mb-12 lg:mb-20">
          <h2 className="mb-10 text-5xl font-black text-white">조직도</h2>
        </div>

        {/* =========================
            데스크톱 버전
        ========================= */}
        <div className="hidden lg:block">
          {/* CEO */}
          <div className="flex justify-center mb-16">
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-full p-8 shadow-2xl border border-blue-400/30 backdrop-blur-sm">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <div className="font-bold text-xl">대표이사</div>
                </div>
              </div>

              {/* 연결선 */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0.5 h-12 bg-gradient-to-b from-blue-400 to-gray-600" />
            </div>
          </div>

          {/* 중간 레벨 */}
          <div className="flex justify-center items-start mb-16">
            <div className="flex items-start gap-16">
              {/* 기업부설연구소 */}
              <div className="relative">
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white rounded-xl p-6 w-72 text-center shadow-xl border border-gray-600/50 hover:border-gray-500/70 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg mx-auto mb-4 flex items-center justify-center shadow-lg">
                    <GraduationCap className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-sm mb-2 text-gray-300">
                    기업부설연구소
                  </div>
                  <div className="font-bold text-lg text-white">연구소장</div>
                </div>

                {/* 연결선 */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0.5 h-8 bg-gradient-to-b from-gray-600 to-transparent" />
              </div>

              {/* 경영지원팀 */}
              <div className="relative">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-xl p-6 w-72 text-center shadow-xl border border-blue-400/50 hover:border-blue-300/70 transition-all duration-300">
                  <div className="w-12 h-12 bg-white/20 rounded-lg mx-auto mb-4 flex items-center justify-center shadow-lg">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-sm mb-2 opacity-90">경영지원팀</div>
                  <div className="font-bold text-lg">경영이사</div>
                </div>

                {/* 연결선 */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0.5 h-8 bg-gradient-to-b from-blue-400 to-transparent" />
              </div>
            </div>
          </div>

          {/* 하위 부서들 */}
          <div className="grid grid-cols-2 gap-16">
            {/* =========================
                연구소 하위 부서들
                ✅ 핵심 기술 영역 → 기술 개발 → M&S 분석
            ========================= */}
            <div className="space-y-6">
              {/* 1) 핵심 기술 영역 */}
              <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-700/50 hover:shadow-2xl hover:border-blue-500/30 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white text-lg">
                    핵심 기술 영역
                  </h3>
                </div>

                <div className="space-y-2">
                  <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                    <div className="text-sm font-medium text-gray-200">
                      무인기 설계
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                    <div className="text-sm font-medium text-gray-200">
                      비행제어/항법시스템 설계
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                    <div className="text-sm font-medium text-gray-200">
                      영상탐색기 시스템 설계
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                    <div className="text-sm font-medium text-gray-200">
                      성능 평가 PILS 설계
                    </div>
                  </div>
                </div>
              </div>

              {/* 2) 기술 개발 (✅ 이미지 내용으로 교체) */}
              <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-700/50 hover:shadow-2xl hover:border-indigo-500/30 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
                    <Cpu className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white text-lg">기술 개발</h3>
                </div>

                {/* 영상탐색기 / 드론으로 분리 */}
                <div className="space-y-4">
                  {/* 영상탐색기 */}
                  <div className="rounded-lg border border-gray-700/50 bg-black/10 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-indigo-400" />
                      <div className="text-sm font-bold text-white">
                        영상탐색기
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-indigo-500/40 transition-colors">
                        <div className="text-sm font-medium text-gray-200">
                          영상 탐색기 시스템 설계
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-indigo-500/40 transition-colors">
                        <div className="text-sm font-medium text-gray-200">
                          신호처리기 설계
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-indigo-500/40 transition-colors">
                        <div className="text-sm font-medium text-gray-200">
                          김벌제어 설계/제작
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-indigo-500/40 transition-colors">
                        <div className="text-sm font-medium text-gray-200">
                          자동표적 인식기/추적기 설계
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-indigo-500/40 transition-colors">
                        <div className="text-sm font-medium text-gray-200">
                          PILS 설계
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 드론 */}
                  <div className="rounded-lg border border-gray-700/50 bg-black/10 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400" />
                      <div className="text-sm font-bold text-white">드론</div>
                    </div>

                    <div className="space-y-2">
                      <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                        <div className="text-sm font-medium text-gray-200">
                          드론 설계
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                        <div className="text-sm font-medium text-gray-200">
                          비행제어/항법 시스템 설계
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                        <div className="text-sm font-medium text-gray-200">
                          PILS 설계
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                        <div className="text-sm font-medium text-gray-200">
                          지상통제장비 설계
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3) M&S 분석 */}
              <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-700/50 hover:shadow-2xl hover:border-blue-500/30 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white text-lg">M&S 분석</h3>
                </div>

                <div className="space-y-2">
                  <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                    <div className="text-sm font-medium text-gray-200">
                      영상탐색기 및 드론 시스템 설계 검증 및 성능 시험
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-lg p-3 border border-gray-600/30 hover:border-blue-500/40 transition-colors">
                    <div className="text-sm font-medium text-gray-200">
                      비행 시험 및 성능 평가 분석
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* =========================
                경영지원팀 하위 부서들
            ========================= */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-gray-700/50 hover:shadow-2xl hover:border-blue-500/30 transition-all duration-300">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
                    <Building className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-white text-xl">
                    마케팅 및 회계
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-900/40 to-indigo-900/40 rounded-lg p-4 border border-blue-500/30 text-center hover:border-blue-400/50 transition-all">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg mx-auto mb-2 flex items-center justify-center shadow-lg">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-sm font-medium text-gray-200">
                      경영 업무
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-lg p-4 border border-indigo-500/30 text-center hover:border-indigo-400/50 transition-all">
                    <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg mx-auto mb-2 flex items-center justify-center shadow-lg">
                      <Calculator className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-sm font-medium text-gray-200">
                      회계 업무
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =========================
            모바일 버전
            ✅ 핵심 기술 영역 → 기술 개발(분리) → M&S 분석
        ========================= */}
        <div className="lg:hidden">
          {/* CEO */}
          <div className="text-center mb-12">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-6 mx-4 shadow-xl border border-blue-400/30">
              <div className="w-16 h-16 bg-white/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div className="font-bold text-xl">대표이사</div>
            </div>
            <div className="w-0.5 h-8 bg-gradient-to-b from-blue-400 to-gray-600 mx-auto" />
          </div>

          <div className="space-y-10 mb-8">
            {/* 기업부설연구소 */}
            <div className="mx-4">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white rounded-2xl p-6 text-center shadow-xl border border-gray-600/50">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div className="text-sm mb-2 text-gray-300">기업부설연구소</div>
                <div className="font-bold text-lg text-white">연구소장</div>
              </div>

              <div className="mt-6 space-y-5">
                {/* 1) 핵심 기술 영역 */}
                <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-gray-700/50">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                      <Eye className="w-4.5 h-4.5 text-white" />
                    </div>
                    <h4 className="font-bold text-white text-base">
                      핵심 기술 영역
                    </h4>
                  </div>

                  <div className="space-y-2">
                    <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                      <div className="text-sm text-gray-200">무인기 설계</div>
                    </div>
                    <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                      <div className="text-sm text-gray-200">
                        비행제어/항법시스템 설계
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                      <div className="text-sm text-gray-200">
                        영상탐색기 시스템 설계
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                      <div className="text-sm text-gray-200">
                        성능 평가 PILS 설계
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2) 기술 개발 (✅ 이미지 내용으로 교체) */}
                <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-gray-700/50">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
                      <Cpu className="w-4.5 h-4.5 text-white" />
                    </div>
                    <h4 className="font-bold text-white text-base">
                      기술 개발
                    </h4>
                  </div>

                  <div className="space-y-4">
                    {/* 영상탐색기 */}
                    <div className="rounded-xl border border-gray-700/50 bg-black/10 p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-indigo-400" />
                        <div className="text-sm font-bold text-white">
                          영상탐색기
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-xl p-3 border border-gray-600/30">
                          <div className="text-sm text-gray-200">
                            영상 탐색기 시스템 설계
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-xl p-3 border border-gray-600/30">
                          <div className="text-sm text-gray-200">
                            신호처리기 설계
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-xl p-3 border border-gray-600/30">
                          <div className="text-sm text-gray-200">
                            김벌제어 설계/제작
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-xl p-3 border border-gray-600/30">
                          <div className="text-sm text-gray-200">
                            자동표적 인식기/추적기 설계
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-gray-700/50 to-indigo-900/30 rounded-xl p-3 border border-gray-600/30">
                          <div className="text-sm text-gray-200">PILS 설계</div>
                        </div>
                      </div>
                    </div>

                    {/* 드론 */}
                    <div className="rounded-xl border border-gray-700/50 bg-black/10 p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-400" />
                        <div className="text-sm font-bold text-white">드론</div>
                      </div>

                      <div className="space-y-2">
                        <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                          <div className="text-sm text-gray-200">드론 설계</div>
                        </div>
                        <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                          <div className="text-sm text-gray-200">
                            비행제어/항법 시스템 설계
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                          <div className="text-sm text-gray-200">PILS 설계</div>
                        </div>
                        <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                          <div className="text-sm text-gray-200">
                            지상통제장비 설계
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3) M&S 분석 */}
                <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-gray-700/50">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                      <CheckCircle className="w-4.5 h-4.5 text-white" />
                    </div>
                    <h4 className="font-bold text-white text-base">M&S 분석</h4>
                  </div>

                  <div className="space-y-2">
                    <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                      <div className="text-sm text-gray-200">
                        영상탐색기 및 드론 시스템 설계 검증 및 성능 시험
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-gray-700/50 to-blue-900/30 rounded-xl p-3 border border-gray-600/30">
                      <div className="text-sm text-gray-200">
                        비행 시험 및 성능 평가 분석
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 경영지원팀 */}
            <div className="mx-4">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-6 text-center shadow-xl border border-blue-400/50">
                <div className="w-12 h-12 bg-white/20 rounded-lg mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div className="text-sm mb-2 opacity-90">경영지원팀</div>
                <div className="font-bold text-lg">경영이사</div>
              </div>

              <div className="mt-6">
                <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-700/50">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Building className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-bold text-white text-lg">
                      마케팅 및 회계
                    </h4>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gradient-to-br from-blue-900/40 to-indigo-900/40 rounded-xl p-4 text-center border border-blue-500/30">
                      <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg mx-auto mb-2 flex items-center justify-center shadow-lg">
                        <TrendingUp className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="text-sm font-medium text-gray-200">
                        경영 업무
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-xl p-4 text-center border border-indigo-500/30">
                      <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg mx-auto mb-2 flex items-center justify-center shadow-lg">
                        <Calculator className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="text-sm font-medium text-gray-200">
                        회계 업무
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* /mobile */}
        </div>
      </div>
    </section>
  );
}
